import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface NotaEntregue {
  filial: string;
  numero: string;
  // Numero do RPS (SF1.F1_NUMRPS). Referencia provisoria - exibido na
  // coluna 'Nº RPS' do listing. Vazio em notas aprovadas pelo portal
  // (o portal grava apenas F1_NFELETR na aprovacao).
  numeroRps?: string;
  // Numero da NF-e (SF1.F1_NFELETR), enviado pelo backend na chave
  // 'numeroNfe'. Exibido na coluna 'Nº NF-e' do listing.
  numeroNfe?: string;
  serie: string;
  fornecedorCod: string;
  fornecedorLoja: string;
  tipo: string;
  fornecedor: string;
  // Emissor extendido
  fornecedorCnpj: string;
  fornecedorFantasia: string;
  fornecedorEndereco: string;
  fornecedorBairro: string;
  fornecedorMunicipio: string;
  fornecedorEstado: string;
  fornecedorCep: string;
  fornecedorPais: string;
  fornecedorComplemento: string;
  // Tomador (filial Protheus corrente)
  tomadorRazaoSocial: string;
  tomadorFantasia: string;
  tomadorEndereco: string;
  tomadorBairro: string;
  tomadorMunicipio: string;
  tomadorEstado: string;
  tomadorCep: string;
  tomadorCnpj: string;
  tomadorPais: string;
  tomadorComplemento: string;
  // Impostos retidos
  impostoPIS: number;
  impostoCOFINS: number;
  impostoIR: number;
  impostoINSS: number;
  impostoCSLL: number;
  status: string;
  total: number;
  emissao: string;
  recebida: string;
  aprovado: string;
  vencimento: string;
  usuario: string;
  codigo: number;
  processo: string;
  tes: number;
  produto: string;
  fin: string;
  origem: string;
  oculto: string;
  // Qtd de anexos ativos na ZOF (backend: QTD_ANEXOS). 0 = nota entregue sem
  // anexo - candidata a reanexo pela modal.
  qtdAnexos: number;
  // Integracao da NFS-e com o Conexos (job PnfNfsJob). Vem vazio enquanto os
  // campos de controle nao existirem no dicionario da SF1.
  //   '' nao avaliada | 1 aguardando envio | 2 criada, falta finalizar
  //   3 integrada | 4 em erro, tentando de novo | 5 aguardando reprocesso
  //   9 fora do escopo
  cnxStatus?: string;
  cnxStatusDes?: string;
  cnxDoc?: string;
  cnxMsg?: string;
  cnxData?: string;
  cnxTentativa?: number;
}

interface ListResponse {
  success: boolean;
  data: NotaEntregue[];
  page?: number;
  pageSize?: number;
  hasNext?: boolean;
}

export interface PageResult<T> {
  data: T[];
  hasNext: boolean;
}

export interface NotasEntreguesFiltros {
  // Busca rapida (barra superior): LIKE (OR) em F1_DOC, F1_NUMRPS e F1_NFELETR.
  busca?: string;
  numero?: string;
  // Numero do RPS (SF1.F1_NUMRPS) - filtro independente do numero da nota.
  rps?: string;
  fornecedor?: string;
  // Codigo da filial Protheus (ex.: '003001001') resolvido a partir do CNPJ
  // do tomador via FilialService.codigoByCnpj. Backend filtra SF1.F1_FILIAL.
  filial?: string;
  tipo?: string;          // 'NFSe' | 'NFe' | 'CTe'
  origem?: string;        // 'Manual' | 'Arquivei'
  fin?: string;           // 'S' | 'N'
  oculto?: string;        // 'Sim' | 'Nao'
  semAnexo?: string;      // 'S' = apenas notas sem anexo ativo na ZOF
  // Situacao da integracao com o Conexos: lista de estados separada por
  // virgula (ex.: '4,5' = apenas as notas em erro).
  cnxStatus?: string;
  processo?: string;
  produto?: string;
  tes?: string;
  usuario?: string;
  codigo?: string;
  emissaoDe?: string;
  emissaoAte?: string;
  recebidaDe?: string;
  recebidaAte?: string;
  aprovadoDe?: string;
  aprovadoAte?: string;
  vencimentoDe?: string;
  vencimentoAte?: string;
  totalDe?: number | string | null;
  totalAte?: number | string | null;
}

export interface ReprovarReq {
  filial: string;
  doc: string;
  serie: string;
  fornec: string;
  loja: string;
  motivo?: string;
}

export interface ReprovarResp {
  success: boolean;
  message?: string;
}

export interface ReprocessarCnxReq {
  filial: string;
  doc: string;
  serie: string;
  fornec: string;
  loja: string;
}

export interface ReprocessarCnxResp {
  success: boolean;
  status?: string;
  statusDes?: string;
  docCod?: string;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class NotasEntreguesService {
  private readonly base = 'portalnf/notas-entregues';

  constructor(private http: HttpClient) {}

  reprovar(req: ReprovarReq): Observable<ReprovarResp> {
    return this.http.post<ReprovarResp>(`${this.base}/reprovar`, req);
  }

  /**
   * Reenvia a nota de servico ao Conexos na hora (o job faz isso sozinho, mas
   * so ate o teto de tentativas). Zera o contador e preserva o documento ja
   * criado - o reprocesso nunca cria um segundo documento.
   */
  reprocessarConexos(req: ReprocessarCnxReq): Observable<ReprocessarCnxResp> {
    return this.http.post<ReprocessarCnxResp>(`${this.base}/reprocessar-conexos`, req);
  }

  /** Lista usuarios ativos do portal (ZOA) para o filtro "Usuario". */
  listarUsuarios(): Observable<{ cod: string; nome: string; email: string }[]> {
    return this.http.get<{ success: boolean; data: { cod: string; nome: string; email: string }[] }>(
      'portalnf/usuarios'
    ).pipe(map(r => r.data ?? []));
  }

  list(page = 1, pageSize = 10, filtros: NotasEntreguesFiltros = {}): Observable<PageResult<NotaEntregue>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    const textFields: Array<keyof NotasEntreguesFiltros> = [
      'busca','numero','rps','fornecedor','filial','tipo','origem','fin','oculto','semAnexo','cnxStatus',
      'processo','produto','tes','usuario','codigo',
      'emissaoDe','emissaoAte','recebidaDe','recebidaAte',
      'aprovadoDe','aprovadoAte','vencimentoDe','vencimentoAte'
    ];
    for (const k of textFields) {
      const v = filtros[k];
      if (v != null && v !== '') params = params.set(k, String(v));
    }
    if (filtros.totalDe  != null && filtros.totalDe  !== '') params = params.set('totalDe',  String(filtros.totalDe));
    if (filtros.totalAte != null && filtros.totalAte !== '') params = params.set('totalAte', String(filtros.totalAte));
    return this.http.get<ListResponse>(this.base, { params }).pipe(
      map(r => ({ data: r.data ?? [], hasNext: !!r.hasNext }))
    );
  }
}
